import { AxiosResponse } from 'axios';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { IResortLayout } from 'page-components/resort/_layout';
import React from 'react';
import { getResortSlug } from 'util/memcache';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import {
  fetchContactInformation,
  fetchResortAccommodations,
  fetchResortDescriptionTabs,
  fetchResortLastFHReport,
  fetchResortPhotos,
  fetchResortRelated,
  fetchResortRelatedNews,
  fetchResortReviews,
  fetchResortReviewsPages,
  fetchResortShortInfo,
  fetchResortShortWeatherReport,
  fetchResortSnowReport,
  fetchResortStrengths,
  fetchResortTrailMap,
  fetchSnowfallInfo,
} from 'api/resort/static';
import {
  HistoricalSnowfallType,
  IContactInformation,
  IDescriptionsResort,
  IFirstHandReport,
  IRelatedNews,
  IResortPageInfo,
  IResortPageSnowInfo,
  IResortPathInfo,
  IResortReviews,
  IResortStrengthsOverview,
  IResortTrailmapPage,
  IReviewsGeneral,
  ISnowfallItem,
  ReviewsOrderBy,
} from 'api/resort/types';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { Main } from 'components/resort/Layout/Main';

import { Hero400Error } from 'components/400';
import { fetchResortMeta } from 'api/region/static';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { useSanitizedHtmlProps } from 'util/sanitized';
import { IAccommodationPage } from 'api/tripadvisor/types';
import { ResortPhotosData } from 'components/widget/types';
import { fetchNearbyResorts } from 'api/home/static';
import { INearbyResort } from 'api/region/types';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { ResortGeneralInfo } from 'components/resort/ResortGeneralInfo';
import { OverviewTopSlider } from 'components/resort/OverviewTopSlider';
import { ImagesSection } from 'components/resort/ImagesSection';
import { HowToGetThereSection } from 'components/resort/HowToGetThereSection';
import { NearbyResortsSection } from 'components/resort/NearbyResortsSection';
import { SidebarAd } from 'googlead/components/SidebarAd';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import ElfsightInstagramWidget from 'components/resort/ElfsightInstagramWidget/Index';
import styles from './ski-resort.module.scss';

const filterOutTabData = (data?: {
  dangerouslySetInnerHTML: {
    __html: string;
  };
}) => {
  if (!data) {
    return data;
  }
  const FORBIDDEN_CHARACTERS = '►';
  const result = data;

  // eslint-disable-next-line no-underscore-dangle
  let html = data.dangerouslySetInnerHTML.__html;
  const forbiddenCharacters = FORBIDDEN_CHARACTERS.split('');
  forbiddenCharacters.forEach((char) => {
    html = html.replace(new RegExp((char), 'g'), '');
  });
  // eslint-disable-next-line no-underscore-dangle
  result.dangerouslySetInnerHTML.__html = html;
  return result;
};

export type SummerOverviewPageProps = {
  resort: IResortPageInfo,
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
  resortStrengths: IResortStrengthsOverview,
  resortDescriptionTabs: Array<IDescriptionsResort>,
  snowfallInfoAnnual: Array<ISnowfallItem> | null,
  snowfallInfoMonthly: Array<ISnowfallItem> | null,
  trailmap: IResortTrailmapPage,
  contactInformation: IContactInformation,
  reviewsGeneral: IReviewsGeneral | null
  relatedNews: IRelatedNews | null,
  initials: {
    firstHand: IFirstHandReport | null,
  },
  accommodations: IAccommodationPage | null,
  photos: ResortPhotosData,
  nearbyResorts: INearbyResort[],
  resortReviews: IResortReviews
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'summer-overview-page';
export const TEMPLATE_ID = '3.2.10';
export const TEMPLATE_NAME = 'Resort Summer Overview';

export default function SummerOverviewPage({
  fullResort,
  pathInfo,
  resortStrengths,
  resortDescriptionTabs,
  trailmap,
  contactInformation,
  photos,
  nearbyResorts,
  snowfallInfoAnnual,
  snowfallInfoMonthly,
  relatedNews,
  resort,
}: SummerOverviewPageProps) {
  const topSliderPhotos = [...photos];

  const tabs = (resortDescriptionTabs.reduce((prev: Record<string, string>, curr) => ({ ...prev, [curr.tab]: (curr.description || '') }), {}));

  const resortDescription = useSanitizedHtmlProps(tabs.resort_summer ?? '');

  const transportationDescription = useSanitizedHtmlProps(tabs.transportation_profile ?? '');

  const { city } = contactInformation.address;
  const { country } = contactInformation.address;

  const totalSnowfall = snowfallInfoAnnual?.reduce((prev, curr) => prev + curr.totalSnow, 0);

  const annualSnowfall = totalSnowfall && snowfallInfoAnnual && Math.ceil(totalSnowfall / snowfallInfoAnnual?.length);

  const { t } = useOTSTranslation('common');

  const instagram = resort.socialMediaAccounts.find((acc) => acc.type === 'instagram');

  if (!fullResort) {
    return <Hero400Error light />;
  }

  const isSummerMode = true;

  return (
    <Main>
      <div className={styles.container}>

        <div className={styles.wrapper}>
          {trailmap && resortStrengths
            && fullResort.forecast && (
              <div className={styles.flex_1}>
                {Boolean(!!instagram && fullResort?.isPartner) && (
                <div style={{ padding: '0 0 19px 19px' }}>
                  <ElfsightInstagramWidget uuid={fullResort.uuid} />
                </div>
                )}
                <ResortGeneralInfo
                  trailmap={trailmap}
                  resortStrengths={resortStrengths}
                  snowfallInfoMonthly={snowfallInfoMonthly}
                  snowfallInfoAnnual={snowfallInfoAnnual}
                  pathInfo={pathInfo}
                  annualSnowfall={annualSnowfall ?? undefined}
                  isSummerMode={isSummerMode}
                />
                <div className={styles.ad_container}>
                  <div className={styles.news_container}>
                    <SurfaceRelatedArticles
                      relatedNewsList={relatedNews}
                      isFresh
                    />
                  </div>
                  <SidebarAd id={pathInfo.uuid} isSummerMode={isSummerMode} />
                </div>
              </div>
          )}
          <section id={styles.main_content}>
            {photos && resortDescription && (
              <OverviewTopSlider
                photos={topSliderPhotos}
                resortDescription={filterOutTabData(resortDescription)}
                title={t('resort.summerOverview.summerTab')}
                isSummerMode={isSummerMode}
              />
            )}

            {photos && photos.length > 2 && (
              <ImagesSection photos={photos.slice(1)} />
            )}

            {transportationDescription && contactInformation && (
              <HowToGetThereSection
                city={city}
                contactInformation={contactInformation}
                country={country}
                description={filterOutTabData(transportationDescription)}
                fullResort={fullResort}
                isSummerMode={isSummerMode}
              />
            )}

            {nearbyResorts
              && <NearbyResortsSection nearbyResorts={nearbyResorts.slice(1)} isSummerMode={isSummerMode} />}
          </section>

        </div>
      </div>

    </Main>
  );
}

export const fetchProps = async ({ resort }: { region: string, resort: string }): Promise<SummerOverviewPageProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));
  const { uuid } = cache.data;
  const res = await fetchResortShortInfo(uuid);
  const fullResort = await fetchResortSnowReport(uuid);
  const resortStrengths = await fetchResortStrengths(uuid);
  const resortDescriptionTabs = await fetchResortDescriptionTabs(uuid);
  const reviewsGeneral = await fetchResortReviews(uuid);
  const snowfallInfoAnnual = await fetchSnowfallInfo(uuid, HistoricalSnowfallType.annual);
  const snowfallInfoMonthly = await fetchSnowfallInfo(uuid, HistoricalSnowfallType.monthly);
  const trailmap = await fetchResortTrailMap(uuid);
  const contactInformation = await fetchContactInformation(uuid);
  const relatedNews = await fetchResortRelatedNews(uuid, true);
  const accommodations = await fetchResortAccommodations(uuid);
  const photos = await fetchResortPhotos(uuid, true);
  const nearbyResorts = await fetchNearbyResorts(fullResort.latitude, fullResort.longitude);
  const resortReviews = await fetchResortReviewsPages(uuid, ReviewsOrderBy.date, 0);

  return {
    type: 'resort',
    route: 'summer-overview',
    resort: res,
    shortWeather: await fetchResortShortWeatherReport(uuid),
    fullResort,
    resortStrengths,
    resortDescriptionTabs,
    reviewsGeneral,
    relatedNews,
    snowfallInfoAnnual: snowfallInfoAnnual ?? null,
    snowfallInfoMonthly: snowfallInfoMonthly ?? null,
    trailmap,
    contactInformation,
    pathInfo: cache.data,
    initials: {
      firstHand: await fetchResortLastFHReport(uuid),
    },

    /**
       * Layout
       */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.SUMMER_OVERVIEW,
      tab: CurrentTab.TRIP_PLANNING,
      head: HeaderNavCurLink.TRIP_PLANNING,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort summer overview',
    hero: {
      title: 'resort.summerOverview.heroTitle',
      showVideo: true,
    },
    accommodations,
    meta: {
      description: 'resort.summerOverview.seoText',
      redirectTo: cache.data.domain ?? null,
      title: 'resort.summerOverview.seoTitle',
      dynamic: (await fetchResortMeta(uuid)),
    },
    photos,
    nearbyResorts,
    resortReviews,
  };
};

export const getSummerOverviewPageStaticProps = wrapStaticProps(fetchProps);

export const getSummerOverviewPageServerSideProps = wrapServerSideProps(fetchProps);
